/* ============================================================================
   <instock-card> — inline size selection + add-to-cart for the In-Stock rail.

   - Selecting a size pill updates the displayed price and the variant the
     "Add to Cart" button will add.
   - Adding posts to the AJAX cart, refreshes the header cart-count bubble and
     the (hidden) cart-drawer markup, and shows a brief "Added" confirmation.
     It does NOT open the cart drawer (shopper stays on the page) — but the
     drawer's contents are still kept in sync so it isn't stale/empty the next
     time the shopper opens it themselves.

   Relies on the theme globals exposed in theme.liquid:
     theme.routes.cartAdd, theme.routes.cart
   ========================================================================== */
class InstockCard extends HTMLElement {
  connectedCallback() {
    this.pillInputs = this.querySelectorAll('.instock-card__pill-input');
    this.atcBtn = this.querySelector('[data-atc]');
    this.priceEl = this.querySelector('[data-atc-price]');
    this.labelEl = this.querySelector('[data-atc-label]');
    this.stockBadge = this.querySelector('[data-stock-badge]');
    this.cartDrawer = document.querySelector('cart-drawer');

    this.onPillChange = this.handlePillChange.bind(this);
    this.onAdd = this.handleAdd.bind(this);

    this.pillInputs.forEach((input) => input.addEventListener('change', this.onPillChange));
    if (this.atcBtn) this.atcBtn.addEventListener('click', this.onAdd);

    // Batching means there is a window where the badge has counted clicks the
    // cart has not received yet. If the shopper navigates or backgrounds the tab
    // inside that window, a normal fetch started during teardown is cancelled and
    // those items are silently lost — the next page load would show the real,
    // lower count with no explanation.
    //
    // visibilitychange -> hidden is the signal to use, not unload or pagehide:
    // iOS Safari frequently skips those when backgrounding an app, but reliably
    // fires this one. The flush it triggers uses keepalive so the request
    // outlives the page.
    this.onHide = () => {
      if (document.visibilityState === 'hidden' && this.pending > 0) {
        clearTimeout(this.flushTimer);
        this.flushPending(true);
      }
    };
    document.addEventListener('visibilitychange', this.onHide);
  }

  disconnectedCallback() {
    this.pillInputs.forEach((input) => input.removeEventListener('change', this.onPillChange));
    if (this.atcBtn) this.atcBtn.removeEventListener('click', this.onAdd);
    clearTimeout(this.resetTimer);
    clearTimeout(this.flushTimer);
    if (this.onHide) document.removeEventListener('visibilitychange', this.onHide);
    // The header count was already bumped for these clicks, so dropping them here
    // would leave the badge overstating the cart. keepalive because the element is
    // being torn down and an ordinary request would not outlive it.
    if (this.pending > 0) this.flushPending(true);
  }

  handlePillChange(event) {
    const input = event.currentTarget;
    if (input.disabled) return;

    this.querySelectorAll('.instock-card__pill').forEach((pill) => pill.classList.remove('is-active'));
    input.closest('.instock-card__pill').classList.add('is-active');

    // Send anything already queued against the previous size before the button
    // starts pointing at a new variant, so a pending batch cannot be attributed
    // to the wrong one.
    if (this.pending > 0) {
      clearTimeout(this.flushTimer);
      this.flushPending();
    }

    if (this.atcBtn) {
      this.atcBtn.dataset.variantId = input.value;
      // The button follows the chosen size: a card that opened on a sold-out
      // size used to stay disabled and say "Sold out" after an in-stock size
      // was picked, so that size could not be added from the card.
      const available = input.dataset.available !== 'false';
      this.atcBtn.disabled = !available;
      const label = available ? this.atcBtn.dataset.labelAdd : this.atcBtn.dataset.labelSoldOut;
      if (this.labelEl && label) {
        this.labelEl.textContent = label;
        // An "Added" confirmation still showing restores to this when it ends.
        this.defaultLabel = label;
      }
      // The stock badge describes the chosen size too, so it never says
      // "In stock" beside a "Sold out" button or the other way round.
      if (this.stockBadge) {
        this.stockBadge.classList.toggle('is-sold', !available);
        const badgeLabel = available ? this.stockBadge.dataset.labelInStock : this.stockBadge.dataset.labelSoldOut;
        if (badgeLabel) this.stockBadge.textContent = badgeLabel;
      }
    }
    if (this.priceEl && input.dataset.price) this.priceEl.textContent = input.dataset.price;
  }

  /**
   * A click only accumulates locally — it does not call the cart. Each click
   * bumps the header count by one and restarts a short timer; when the clicking
   * stops, one request adds the whole accumulated quantity.
   *
   * Firing a request per click was the problem: ten taps meant ten concurrent
   * POSTs, and each response carries the authoritative cart count as it stood
   * when THAT response was rendered. They land out of order, so the badge walked
   * 1,2,3,4,5,4,3,2,1,2,3,4,6 — every late response stamping a stale total over a
   * newer one. Coalescing to a single request removes the race entirely: the
   * count rises 1..10 optimistically and exactly one authoritative value lands at
   * the end.
   */
  handleAdd() {
    if (!this.atcBtn || this.atcBtn.disabled) return;

    const variantId = this.atcBtn.dataset.variantId;
    if (!variantId) return;

    // A different size was picked mid-batch: send what is queued for the old
    // variant before starting to count against the new one.
    if (this.pendingVariant && this.pendingVariant !== variantId) {
      clearTimeout(this.flushTimer);
      this.flushPending();
    }
    this.pendingVariant = variantId;

    this.pending = (this.pending || 0) + 1;
    InstockCard.bumpCount(1);
    this.confirm(false, this.pending);

    clearTimeout(this.flushTimer);
    this.flushTimer = setTimeout(() => this.flushPending(), 550);
  }

  /**
   * Sends the accumulated quantity as one request.
   * Never runs two at once: if clicks arrive while a request is in flight they
   * queue into the next batch, which the finally block picks up.
   */
  async flushPending(keepalive = false) {
    if (this.inFlight) return;

    const quantity = this.pending || 0;
    const variantId = this.pendingVariant;
    this.pending = 0;
    if (!quantity || !variantId) return;

    this.inFlight = true;

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', quantity);

    // Only the header count bubble. Asking for the whole cart-drawer section made
    // the server render line items, recommendations and the shipping estimator
    // before responding, on a path that never opens the drawer.
    formData.append('sections', 'cart-icon-bubble');
    formData.append('sections_url', window.location.pathname);

    try {
      const response = await fetch(theme.routes.cartAdd, {
        method: 'POST',
        headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
        // Set only on the teardown path. keepalive lets the request complete after
        // the page goes away; the payload is a few bytes, well inside its 64KB cap.
        keepalive,
      });
      const data = await response.json();
      if (!response.ok || data.status) {
        throw new Error(data.description || data.message || 'Add to cart failed');
      }

      // The authoritative count. Because this is the only request in flight, it
      // cannot be overwritten by a stale sibling. The Section Rendering API wraps
      // output in a .shopify-section div, so unwrap it rather than nesting a
      // second wrapper inside #cart-icon-bubble.
      const bubbleHTML = data.sections && data.sections['cart-icon-bubble'];
      const bubble = document.getElementById('cart-icon-bubble');
      if (bubble && bubbleHTML) {
        const tmpl = document.createElement('template');
        tmpl.innerHTML = bubbleHTML;
        const inner = tmpl.content.querySelector('.shopify-section');
        bubble.innerHTML = inner ? inner.innerHTML : bubbleHTML;
      }

      // Resync the closed drawer in the background so it is not stale next time
      // it is opened. Deliberately not awaited.
      if (this.cartDrawer) {
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));
      }
    } catch (error) {
      console.log(error); // eslint-disable-line
      // Take back exactly what this batch added optimistically.
      InstockCard.bumpCount(-quantity);
      this.confirm(true);
    } finally {
      this.inFlight = false;
      // Clicks that arrived during the request form the next batch.
      if (this.pending > 0) this.flushPending();
    }
  }

  /**
   * Adjusts the header cart count immediately, before the server has confirmed.
   * Accepts a negative delta so a failed batch can take back exactly what it
   * added — the previous approach snapshotted innerHTML and restored it, which no
   * longer works now that several clicks mutate the badge before a request runs.
   * @param {number} delta
   */
  static bumpCount(delta) {
    const bubble = document.getElementById('cart-icon-bubble');
    if (!bubble) return;

    const numberEl = bubble.querySelector('.header__cart-count-number');

    if (numberEl) {
      const current = parseInt(numberEl.textContent.trim(), 10);
      if (Number.isNaN(current)) return;
      const next = Math.max(0, current + delta);
      if (next === 0) {
        // Back to an empty cart: the section renders nothing in that state, so
        // clearing matches what a page load would produce.
        bubble.innerHTML = '';
      } else {
        numberEl.textContent = next;
      }
      return;
    }

    if (delta <= 0) return;

    // Cart was empty, so the section rendered nothing and there is no badge yet.
    // Mirror the markup in sections/cart-icon-bubble.liquid, including
    // header__cart-count-number — without that class the badge loses its styling.
    bubble.innerHTML =
      '<div class="header__cart-count absolute bg-theme-text text-theme-bg text-center">'
      + `<span aria-hidden="true" class="header__cart-count-number">${delta}</span>`
      + '</div>';
  }

  confirm(isError = false, count = 1) {
    if (!this.labelEl) return;
    if (!this.defaultLabel) this.defaultLabel = this.labelEl.textContent;

    // Pin the label to its natural width before swapping the text. "Added" is
    // much shorter than "Add to cart", and in list layout the button is
    // width:auto, so the swap resized the button and shifted the row — the jump
    // is what made the confirmation look glitchy rather than the colour change.
    // Measured once, while the label still holds its original text.
    if (!this.labelPinned) {
      const w = this.labelEl.getBoundingClientRect().width;
      if (w) {
        this.labelEl.style.minWidth = `${Math.ceil(w)}px`;
        this.labelPinned = true;
      }
    }

    this.atcBtn.classList.toggle('is-added', !isError);
    this.atcBtn.classList.toggle('is-error', isError);
    // Show the running total while a batch accumulates, so ten quick taps read as
    // "Added x10" rather than the label flickering on each one with no indication
    // that anything beyond the first registered.
    this.labelEl.textContent = isError
      ? 'Try again'
      : (count > 1 ? `Added \u00d7${count}` : 'Added');

    clearTimeout(this.resetTimer);
    // Short hold: the state now appears on click rather than a second later, so
    // the whole interaction is over quickly and a long green tail just reads as
    // the button being stuck.
    this.resetTimer = setTimeout(() => {
      this.labelEl.textContent = this.defaultLabel;
      this.atcBtn.classList.remove('is-added', 'is-error');
    }, 600);
  }
}

if (!customElements.get('instock-card')) {
  customElements.define('instock-card', InstockCard);
}
